import { useCallback, useEffect, useState } from 'react';
import { Loader2, UserPlus, Users, X } from 'lucide-react';
import { practiceApi, errorDetailText } from '../../services/api';
import { Collaborator } from '../../types';
import { Badge, Button } from '../ui';

/**
 * Case collaborators (juniors / co-counsel): list + invite by email + revoke.
 * Controls hide on shared cases (`readOnly`) — only the owner manages access.
 */
export function CollaboratorList({ caseId, readOnly }: { caseId: string; readOnly?: boolean }) {
  const [collabs, setCollabs] = useState<Collaborator[] | null>(null);
  const [email, setEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    practiceApi
      .listCollaborators(caseId)
      .then(({ data }) => setCollabs(data))
      .catch(() => setCollabs([]));
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || isInviting) return;
    setIsInviting(true);
    setError('');
    try {
      await practiceApi.invite(caseId, value);
      setEmail('');
      load();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not send that invite.'));
    } finally {
      setIsInviting(false);
    }
  };

  const revoke = async (collabId: string) => {
    if (revokingId) return;
    setRevokingId(collabId);
    setError('');
    try {
      await practiceApi.revoke(caseId, collabId);
      load();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not remove that collaborator.'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="bg-white rounded-card border border-legal-200 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-legal-800 mb-3">
        <Users className="h-4 w-4 text-legal-500" /> Collaborators
      </p>

      {collabs === null ? (
        <p className="text-xs text-legal-400">Loading…</p>
      ) : collabs.length === 0 ? (
        <p className="text-xs text-legal-400 mb-2">
          {readOnly
            ? 'Only the case owner manages collaborators.'
            : 'No one else on this case yet — invite a junior or co-counsel by email.'}
        </p>
      ) : (
        <ul className="divide-y divide-legal-100 mb-2">
          {collabs.map((c) => (
            <li key={c.id} className="flex items-center gap-2 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-legal-800 truncate">
                  {c.collaborator_name || c.invited_email}
                </p>
                {c.collaborator_name && (
                  <p className="text-[11px] text-legal-400 truncate">{c.invited_email}</p>
                )}
              </div>
              <Badge tone={c.status === 'active' ? 'success' : 'warning'}>
                {c.status === 'active' ? 'Active' : 'Invited'}
              </Badge>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => revoke(c.id)}
                  disabled={revokingId === c.id}
                  aria-label={`Remove ${c.collaborator_name || c.invited_email}`}
                  className="p-1.5 rounded-md text-legal-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {revokingId === c.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <X className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <form onSubmit={invite} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="junior@chambers.in"
            className="flex-1 min-w-0 text-sm border border-legal-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <Button
            type="submit"
            variant="secondary"
            size="md"
            disabled={!email.trim() || isInviting}
            className="shrink-0 px-3"
          >
            {isInviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            <span className="hidden sm:inline">Invite</span>
          </Button>
        </form>
      )}

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
    </div>
  );
}
